import { useId, useState } from 'react';
import type { DashboardDetail, DashboardNote } from '../dashboardTypes';
import { formatInstant } from '../format';
import { describeFailure } from './failures';
import { taxPaymentsGateway } from '../index';

const LONG_NUMBER = /\d[\d\s-]{7,}\d/;
export const NOTE_MAX = 1000;

/**
 * Internal responsibility notes (Figma S11, S14). The server sends `notes` only to a caller who holds the comment permission, so a null list means "not allowed to
 * see or add notes": nothing is shown and nothing is guessed. A note is saved by the server (author role and time are the server's), and the list comes back from
 * the server's answer; it is never added to the screen first. Never shown to the client company.
 */
export function NotesPanel({ paymentId, notes, onSaved, onDraftChange }: {
  paymentId: string;
  notes: DashboardNote[] | null;
  onSaved: (updated: DashboardDetail) => void;
  onDraftChange: (hasDraft: boolean) => void;
}) {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (notes === null) {
    return (
      <section className="tpd-section" aria-labelledby={`${uid}-h`}>
        <h4 id={`${uid}-h`} className="tpd-subhead">Internal responsibility notes</h4>
        <p className="tpd-muted" data-testid="notes-hidden">Internal notes are visible only to people who may comment on tax payments.</p>
      </section>
    );
  }

  const change = (value: string) => { setText(value); setError(null); onDraftChange(value.trim().length > 0); };
  const cancel = () => { setOpen(false); change(''); };

  async function add() {
    const clean = text.trim();
    if (clean.length < 1) return setError('Enter a comment before adding it.');
    if (clean.length > NOTE_MAX) return setError(`The comment must be ${NOTE_MAX} characters or fewer.`);
    if (LONG_NUMBER.test(clean)) return setError('Don’t enter an account or other long number in a comment.');
    setBusy(true);
    setError(null);
    try {
      onSaved(await taxPaymentsGateway.addNote(paymentId, clean));
      setOpen(false);
      change('');
    } catch (err) {
      setError(describeFailure(err, 'The comment couldn’t be added. Try again.').message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="tpd-section" aria-labelledby={`${uid}-h`}>
      <h4 id={`${uid}-h`} className="tpd-subhead">Internal responsibility notes</h4>
      {notes.length === 0 ? <p className="tpd-muted" data-testid="notes-empty">No internal notes yet.</p> : (
        <ul className="tpd-notes" aria-label="Internal notes">
          {notes.map((n) => (
            <li key={n.id} className="tpd-note">
              <p>{n.text}</p>
              <p className="tpd-muted">{n.authorRole} · {formatInstant(n.createdAtUtc)}</p>
            </li>
          ))}
        </ul>
      )}
      {!open ? (
        <button type="button" className="tpd-link" onClick={() => setOpen(true)}>Add Comment</button>
      ) : (
        <div className="tpd-composer">
          <label htmlFor={`${uid}-t`}>Add internal comment</label>
          <textarea id={`${uid}-t`} data-testid="note-input" rows={3} maxLength={NOTE_MAX} value={text} disabled={busy} aria-invalid={error ? true : undefined} aria-describedby={error ? `${uid}-e` : undefined} onChange={(e) => change(e.target.value)} />
          <span className="tpd-muted" aria-live="polite">{text.trim().length} / {NOTE_MAX}</span>
          {error && <p className="jr-error" id={`${uid}-e`} role="alert">{error}</p>}
          <div className="tpd-composer__actions">
            <button type="button" className="tpd-btn tpd-btn--outline" disabled={busy} onClick={cancel}>Cancel</button>
            <button type="button" className="tpd-btn tpd-btn--primary" disabled={busy || text.trim().length === 0} onClick={() => void add()}>{busy ? 'Adding…' : 'Add Comment'}</button>
          </div>
        </div>
      )}
    </section>
  );
}
